import SentrySingleton from '../services/SentrySingleton';
import { logAndCaptureError, logAndCaptureWarning } from './sentryErrorCapture';

/**
 * Centralized logger utility that automatically sends appropriate logs to Sentry
 * Replaces console.log, console.warn, console.error, console.debug throughout the app
 */

export interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  additionalData?: Record<string, any>;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;

  /**
   * Convert any value to a string for logging
   */
  private stringify(value: any): string {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.message;
    if (typeof value === 'object' && value !== null) {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  /**
   * Get current user context from Session Storage
   */
  private getCurrentUserContext(): {
    userId?: string;
    accountId?: string;
    accountName?: string;
    schoolId?: string;
    schoolName?: string;
  } {
    try {
      const storedConfig = sessionStorage.getItem('sentry_tracking_config');
      if (storedConfig) {
        const config = JSON.parse(storedConfig);
        return {
          userId: config.userId || undefined,
          accountId: config.accountId || undefined,
          accountName: config.accountName || undefined,
          schoolId: config.schoolId || undefined,
          schoolName: config.schoolName || undefined,
        };
      }
      return {};
    } catch {
      return {};
    }
  }

  /**
   * Get current user ID from Session Storage (backward compatibility)
   */
  private getCurrentUserId(): string | undefined {
    return this.getCurrentUserContext().userId;
  }

  /**
   * Debug logging - only shows in development, not sent to Sentry
   */
  debug(message: any, data?: any, context?: LogContext) {
    const messageStr = this.stringify(message);
    
    // Always log to console in development
    if (this.isDevelopment) {
      console.debug(`[DEBUG] ${messageStr}`, data);
    }
    
    // Don't send debug logs to Sentry to avoid noise
  }

  /**
   * Info logging - shows in console, optionally sent to Sentry for important info
   */
  info(message: any, data?: any, context?: LogContext | any) {
    const messageStr = this.stringify(message);
    console.log(`[INFO] ${messageStr}`, data);
    
    // Handle case where context might be a string or other type
    let logContext: LogContext | undefined;
    if (context && typeof context === 'object' && !Array.isArray(context)) {
      logContext = context as LogContext;
    }
    
    // Only send important info to Sentry (when proper context is provided)
    if (logContext && (logContext.component || logContext.action)) {
      try {
        const Sentry = SentrySingleton.getInstance().getSentry();
        Sentry.withScope(scope => {
          scope.setTag('log_level', 'info');
          if (logContext?.component) scope.setTag('component', logContext.component);
          if (logContext?.action) scope.setTag('action', logContext.action);
          
          // Get user context from session storage
          const userContext = this.getCurrentUserContext();
          const userId = logContext?.userId || userContext.userId;
          if (userId) scope.setTag('user_id', userId);
          
          // Add account info if available
          if (userContext.accountId) {
            scope.setTag('account_id', String(userContext.accountId));
            if (userContext.accountName) scope.setTag('account_name', userContext.accountName);
          }
          
          // Add school info if available
          if (userContext.schoolId) {
            scope.setTag('school_id', String(userContext.schoolId));
            if (userContext.schoolName) scope.setTag('school_name', userContext.schoolName);
          }
          
          scope.setContext('log_context', {
            message: messageStr,
            data,
            context: typeof context === 'string' ? context : undefined,
            userId,
            accountId: userContext.accountId,
            accountName: userContext.accountName,
            schoolId: userContext.schoolId,
            schoolName: userContext.schoolName,
            ...logContext?.additionalData
          });
          
          scope.setLevel('info');
          Sentry.captureMessage(messageStr, 'info');
        });
      } catch (error) {
        console.error('[Logger] Error sending info to Sentry:', error);
      }
    }
  }

  /**
   * Warning logging - shows in console, sent to Sentry for tracking
   */
  warn(message: any, data?: any, context?: LogContext | any) {
    const messageStr = this.stringify(message);
    console.warn(`[WARN] ${messageStr}`, data);
    
    // Handle case where context might be a string or other type
    let logContext: LogContext | undefined;
    if (context && typeof context === 'object' && !Array.isArray(context)) {
      logContext = context as LogContext;
    }
    
    // Use centralized warning capture function with user ID
    try {
      const contextData = {
        component: logContext?.component,
        action: logContext?.action,
        ...logContext?.additionalData,
        ...(typeof context === 'string' ? { context } : {}),
        data
      };
      
      // Get user ID from context or fallback to getCurrentUserId
      const userId = logContext?.userId || this.getCurrentUserId();
      logAndCaptureWarning(messageStr, data, contextData, userId);
    } catch (error) {
      console.error('[Logger] Error sending warning to Sentry:', error);
    }
  }

  /**
   * Error logging - shows in console, always sent to Sentry
   */
  error(message: any, error?: Error | any, context?: LogContext | any) {
    const messageStr = this.stringify(message);
    console.error(`[ERROR] ${messageStr}`, error);
    
    // Handle case where context might be a string or other type
    let logContext: LogContext | undefined;
    if (context && typeof context === 'object' && !Array.isArray(context)) {
      logContext = context as LogContext;
    }
    
    // Use centralized error capture function with user ID
    try {
      const contextData = {
        component: logContext?.component,
        action: logContext?.action,
        ...logContext?.additionalData,
        ...(typeof context === 'string' ? { context } : {})
      };
      
      // Get user ID from context or fallback to getCurrentUserId
      const userId = logContext?.userId || this.getCurrentUserId();
      logAndCaptureError(messageStr, error, contextData, userId);
    } catch (sentryError) {
      console.error('[Logger] Error sending error to Sentry:', sentryError);
    }
  }

  /**
   * Log method for backward compatibility with existing console.log calls
   */
  log(message: any, data?: any, context?: LogContext | any) {
    this.info(message, data, context);
  }
}

// Create singleton instance
export const logger = new Logger();

// Export individual methods for easier migration
export const logDebug = logger.debug.bind(logger);
export const logInfo = logger.info.bind(logger);
export const logWarn = logger.warn.bind(logger);
export const logError = logger.error.bind(logger);
export const log = logger.log.bind(logger);

// Default export
export default logger;
